import { FC } from 'react';

import { useFormik } from 'formik';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { ExportTournamentResultsForm } from './form';

import { tournamentsService } from 'v2/api/tournamentsService';
import { CheckBoxAutocomplete } from 'v2/components/autocomplete/checkBoxAutocomplete/CheckBoxAutocomplete';
import { USER_ROLES, UserRole } from 'v2/constants';
import { TournamentModel } from 'v2/types/TournamentModel';
import { handleHttpError } from 'v2/utils';

interface ExportTournamentResultsDialogProps {
  tournament: TournamentModel;
  isOpen: boolean;
  onClose: VoidFunction;
}

export const ExportTournamentResultsDialog: FC<ExportTournamentResultsDialogProps> = ({
  tournament,
  isOpen,
  onClose,
}) => {
  const { trigger, isMutating } = useSWRMutation(
    ['exportTournamentResults', tournament.id],
    tournamentsService.exportTournamentResults,
    {
      onSuccess: () => {
        handleClose();
      },
      onError: handleHttpError,
    }
  );

  const formik = useFormik<ExportTournamentResultsForm>({
    initialValues: { limit: 1, roles: [UserRole.DEFAULT_USER] },
    onSubmit: (values) => {
      trigger({ limit: values.limit, roles: values.roles.toString() });
    },
  });

  const handleChangeRoles = (values: UserRole[]) => {
    formik.setFieldValue('roles', values);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Export Tournament Results</DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent className="form">
          <TextField
            name="limit"
            value={formik.values.limit}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Limit"
            type="number"
            required
          />

          <CheckBoxAutocomplete
            label="Roles"
            value={formik.values.roles}
            options={USER_ROLES}
            onChange={handleChangeRoles}
            required
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button type="submit" variant="contained" loading={isMutating}>
            Export
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
